import {
  Injectable,
  OnDestroy,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { WalletManager } from './wallet.manager';
import {
  Kcc20Notification,
  Kcc20NotificationsApiService,
} from '../services/notifications/kcc20-notifications-api.service';

const NOTIFICATIONS_PAGE_SIZE = 10;
const NOTIFICATIONS_POLL_MS = 30_000;

/**
 * Single source of truth for the signed-in wallet's notification inbox,
 * shared by the nav bar bell dropdown and the account page inbox card so
 * they don't run independent polling loops against the same endpoint.
 * Polls every 30s while a wallet is signed in, matching front-v2's
 * `refetchInterval`.
 */
@Injectable({ providedIn: 'root' })
export class Kcc20NotificationsManager implements OnDestroy {
  private readonly notificationsApi = inject(Kcc20NotificationsApiService);
  private readonly walletManager = inject(WalletManager);

  private pollHandle?: ReturnType<typeof setInterval>;
  private loadSequence = 0;
  private lastOwner: string | undefined;

  readonly notifications = signal<Kcc20Notification[]>([]);
  readonly total = signal(0);
  readonly unreadTotal = signal(0);
  private readonly unreadTotalKnown = signal(false);
  readonly loading = signal(false);
  readonly loadingMore = signal(false);
  readonly error = signal<string | undefined>(undefined);

  readonly hasMore = computed(() => this.total() > this.notifications().length);
  readonly canMarkAllRead = computed(
    () =>
      Boolean(this.currentOwner()) &&
      (this.unreadTotal() > 0 ||
        (this.unreadTotalKnown() === false && this.hasMore())),
  );

  readonly unreadCount = computed(() => this.unreadTotal());

  constructor() {
    effect(() => {
      const owner = this.walletManager.signedWalletSignal()?.kcc20Owner;
      if (owner === this.lastOwner) return;
      this.lastOwner = owner;
      this.onOwnerChanged(owner);
    });
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  async refresh(): Promise<void> {
    if (!this.currentOwner()) return;
    await this.load(
      this.loadSequence,
      Math.max(NOTIFICATIONS_PAGE_SIZE, this.notifications().length),
    );
  }

  async loadMore(): Promise<void> {
    const owner = this.currentOwner();
    if (!owner || this.loading() || this.loadingMore() || !this.hasMore()) {
      return;
    }

    const sequence = this.loadSequence;
    const offset = this.notifications().length;
    this.loadingMore.set(true);
    this.error.set(undefined);
    try {
      const page = await firstValueFrom(
        this.notificationsApi.getMine(
          { limit: NOTIFICATIONS_PAGE_SIZE, offset },
          this.requestId('load-more'),
        ),
      );
      if (sequence !== this.loadSequence || owner !== this.currentOwner()) {
        return;
      }
      const seen = new Set(this.notifications().map((item) => item.messageId));
      const next = (page.items ?? []).filter(
        (item) => !seen.has(item.messageId),
      );
      const merged = [...this.notifications(), ...next];
      this.notifications.set(merged);
      this.total.set(page.total ?? offset + next.length);
      this.applyUnreadTotal(page, merged);
    } catch {
      if (sequence === this.loadSequence && owner === this.currentOwner()) {
        this.error.set('More notifications could not be loaded.');
      }
    } finally {
      if (sequence === this.loadSequence && owner === this.currentOwner()) {
        this.loadingMore.set(false);
      }
    }
  }

  async markRead(messageId: string): Promise<void> {
    const owner = this.currentOwner();
    const notification = this.notifications().find(
      (item) => item.messageId === messageId,
    );
    if (!owner || !notification || notification.status === 'read') return;

    try {
      await firstValueFrom(
        this.notificationsApi.markMineRead(
          messageId,
          this.requestId('mark-read'),
        ),
      );
    } catch {
      return;
    }
    if (owner !== this.currentOwner()) return;
    this.notifications.update((items) =>
      items.map((item) =>
        item.messageId === messageId ? { ...item, status: 'read' } : item,
      ),
    );
    this.unreadTotal.update((count) => Math.max(0, count - 1));
  }

  async markAllRead(): Promise<void> {
    const owner = this.currentOwner();
    if (!owner || !this.canMarkAllRead()) return;

    try {
      await firstValueFrom(
        this.notificationsApi.markMineAllRead(this.requestId('mark-all-read')),
      );
    } catch {
      return;
    }
    if (owner !== this.currentOwner()) return;
    this.notifications.update((items) =>
      items.map((item) => ({ ...item, status: 'read' })),
    );
    this.unreadTotal.set(0);
    this.unreadTotalKnown.set(true);
  }

  async clear(): Promise<void> {
    const owner = this.currentOwner();
    if (!owner || this.notifications().length === 0) return;

    try {
      await firstValueFrom(
        this.notificationsApi.clearMine(this.requestId('clear')),
      );
    } catch {
      return;
    }
    if (owner !== this.currentOwner()) return;
    this.notifications.set([]);
    this.total.set(0);
    this.unreadTotal.set(0);
    this.unreadTotalKnown.set(true);
  }

  private onOwnerChanged(owner: string | undefined): void {
    this.stopPolling();
    const sequence = ++this.loadSequence;
    if (!owner) {
      this.notifications.set([]);
      this.total.set(0);
      this.unreadTotal.set(0);
      this.unreadTotalKnown.set(false);
      this.error.set(undefined);
      return;
    }
    void this.load(sequence);
    this.pollHandle = setInterval(
      () =>
        void this.load(
          sequence,
          Math.max(NOTIFICATIONS_PAGE_SIZE, this.notifications().length),
        ),
      NOTIFICATIONS_POLL_MS,
    );
  }

  private stopPolling(): void {
    if (this.pollHandle !== undefined) {
      clearInterval(this.pollHandle);
      this.pollHandle = undefined;
    }
  }

  private async load(
    sequence: number,
    limit = NOTIFICATIONS_PAGE_SIZE,
  ): Promise<void> {
    if (sequence !== this.loadSequence) return;
    this.loading.set(true);
    this.error.set(undefined);
    try {
      const page = await firstValueFrom(
        this.notificationsApi.getMine(
          { limit, offset: 0 },
          this.requestId('load'),
        ),
      );
      if (sequence !== this.loadSequence) return;
      this.notifications.set(page.items ?? []);
      this.total.set(page.total ?? page.items?.length ?? 0);
      this.applyUnreadTotal(page, page.items ?? []);
    } catch {
      if (sequence !== this.loadSequence) return;
      this.notifications.set([]);
      this.total.set(0);
      this.unreadTotal.set(0);
      this.unreadTotalKnown.set(false);
      this.error.set('Notifications are unavailable right now.');
    } finally {
      if (sequence === this.loadSequence) this.loading.set(false);
    }
  }

  private currentOwner(): string | undefined {
    return this.walletManager.signedWalletSignal()?.kcc20Owner;
  }

  private requestId(scope: string): string {
    return `kcc20-fe-notifications-${scope}-${Date.now()}`;
  }

  private applyUnreadTotal(
    page: { unreadTotal?: number; unreadCount?: number },
    loadedItems: Kcc20Notification[],
  ): void {
    const unreadTotal = page.unreadTotal ?? page.unreadCount;
    if (typeof unreadTotal === 'number' && Number.isFinite(unreadTotal)) {
      this.unreadTotal.set(Math.max(0, unreadTotal));
      this.unreadTotalKnown.set(true);
      return;
    }
    this.unreadTotal.set(this.loadedUnreadCount(loadedItems));
    this.unreadTotalKnown.set(false);
  }

  private loadedUnreadCount(items: readonly Kcc20Notification[]): number {
    return items.filter((notification) => notification.status !== 'read')
      .length;
  }
}
